import type { Application } from "pixi.js";
import type { Canvas } from "../core/Canvas";
import { SidePanel } from "./components/SidePanel";
import { TopBar } from "./components/TopBar";
import { Toolbar } from "./elements/Toolbar";
import { DebugPanel } from "./components/DebugPanel";


export class UIManager {
    private elements: HTMLElement[] = [];
    private canvas: Canvas;
    private isMobileMenuOpen: boolean = false;
    public app: Application;
    private mediaQuery: MediaQueryList | null = null;
    private debugPanel: DebugPanel | null = null;

    constructor(canvas: Canvas, app: Application) {
        this.app = app;
        this.canvas = canvas;
        this.elements.push(new TopBar(canvas.shapeManager, canvas.canvasCommandManager, canvas.canvasToolModeManager, canvas.canvasViewportManager).getElement());
        this.elements.push(new SidePanel(canvas.shapeManager, canvas.canvasCommandManager, canvas.canvasViewportManager).getElement());
        this.elements.push(new Toolbar().getElement());
        
        // Initialize debug panel
        this.debugPanel = new DebugPanel(canvas);
        this.elements.push(this.debugPanel.getElement());
    }
    
    private mountUI() {
        this.elements.forEach(element => {
            document.body.appendChild(element);
        });
    }
    
    private setupResponsiveHandling() {
        this.mediaQuery = window.matchMedia('(max-width: 768px)');
        this.handleResize(this.mediaQuery);
        this.mediaQuery.addEventListener('change', (e) => this.handleResize(e));
    }
    
    private handleResize(e: MediaQueryListEvent | MediaQueryList) {
        const isMobile = e.matches;
        document.body.classList.toggle('is-mobile', isMobile);
        if (!isMobile && this.isMobileMenuOpen) {
            this.toggleMobileMenu();
        }
    }
    
    private toggleMobileMenu() {
        this.isMobileMenuOpen = !this.isMobileMenuOpen;
        document.body.classList.toggle('menu-open', this.isMobileMenuOpen);
    }

    public getCanvas(): Canvas {
        return this.canvas;
    }

    public init() {
        this.mountUI();
        this.setupResponsiveHandling();
    }

    public cleanup(): void {
        try {
            // Clean up debug panel
            if (this.debugPanel) {
                this.debugPanel.cleanup();
                this.debugPanel = null;
            }
            
            // Remove media query listener
            if (this.mediaQuery) {
                this.mediaQuery.removeEventListener('change', this.handleResize);
                this.mediaQuery = null;
            }

            // Remove all UI elements from DOM
            this.elements.forEach(element => {
                if (element && element.parentNode) {
                    element.parentNode.removeChild(element);
                }
            });
            this.elements = [];

            // Reset body classes
            document.body.classList.remove('is-mobile', 'menu-open');
        } catch (error) {
            console.warn('Error during UI cleanup:', error);
        }
    }
}
